import { doc, getDoc, getDocs, limit } from "firebase/firestore";
import { runtimeDb as db } from "../../firestoreStudio/runtime/runtimeDb";
import { DELETE_BATCH_SIZE } from "../constants";
import { registerCollection, touchCollection } from "../../../services/collectionRegistryService";
import {
  buildConditionQuery,
  countMatchingDocuments,
  fetchMatchingPage,
} from "./conditionQueryService";
import { createBackupsBatched } from "./backupService";
import { commitBatches } from "./firestoreDataService";
import { ensureFreshAuthToken } from "./operationAuthService";
import { estimateBatchMeta } from "./costEstimatorService";

export function applyUpdateTransform(data, request) {
  const next = { ...data };
  const field = request.targetField;
  const newField = request.newFieldName || request.renameTo;
  const val = request.newValue;

  switch (request.updateType) {
    case "setValue":
    case "replaceValue":
      next[field] = coerceUpdateValue(val);
      break;
    case "increment":
      next[field] = Number(next[field] || 0) + Number(val || 0);
      break;
    case "decrement":
      next[field] = Number(next[field] || 0) - Number(val || 0);
      break;
    case "appendText":
      next[field] = String(next[field] ?? "") + String(val ?? "");
      break;
    case "removeText":
      next[field] = String(next[field] ?? "").split(String(val ?? "")).join("");
      break;
    case "uppercase":
      if (typeof next[field] === "string") next[field] = next[field].toUpperCase();
      break;
    case "lowercase":
      if (typeof next[field] === "string") next[field] = next[field].toLowerCase();
      break;
    case "trim":
      if (typeof next[field] === "string") next[field] = next[field].trim();
      break;
    case "setNull":
      next[field] = null;
      break;
    case "deleteField":
      delete next[field];
      break;
    case "renameField":
      if (field in next && newField) {
        next[newField] = next[field];
        delete next[field];
      }
      break;
    case "copyField":
      if (request.sourceField) next[field] = next[request.sourceField];
      break;
    case "moveField":
      if (request.sourceField && newField) {
        next[newField] = next[request.sourceField];
        delete next[request.sourceField];
      }
      break;
    default:
      break;
  }
  return next;
}

function coerceUpdateValue(raw) {
  const s = String(raw ?? "").trim();
  if (s === "true") return true;
  if (s === "false") return false;
  if (s === "null") return null;
  if (s !== "" && !Number.isNaN(Number(s)) && /^-?\d+(\.\d+)?$/.test(s)) return Number(s);
  return raw;
}

export async function dryRunUpdateByCondition(collectionName, filter, updateRequest) {
  const col = collectionName.trim();
  if (!updateRequest.targetField?.trim()) throw new Error("Target field is required");

  const matchCount = await countMatchingDocuments(col, filter);
  const previewSnap = await getDocs(buildConditionQuery(col, filter, [limit(20)]));
  const previewRows = previewSnap.docs.map((d) => {
    const before = d.data() || {};
    const after = applyUpdateTransform(before, updateRequest);
    return {
      id: d.id,
      before,
      after,
      oldValue: before[updateRequest.targetField],
      newValue: after[updateRequest.targetField ?? updateRequest.renameTo],
    };
  });

  const batchMeta = estimateBatchMeta(matchCount, DELETE_BATCH_SIZE);
  const changedFields = new Set([updateRequest.targetField]);
  if (updateRequest.renameTo) changedFields.add(updateRequest.renameTo);
  if (updateRequest.sourceField) changedFields.add(updateRequest.sourceField);

  const warnings = [];
  if (matchCount > 5000) warnings.push(`Large update: ${matchCount.toLocaleString()} documents.`);
  if (matchCount === 0) warnings.push("No documents match the filter.");

  return {
    affectedDocuments: matchCount,
    previewIds: previewRows.map((r) => r.id),
    previewRows,
    changedFields: [...changedFields],
    ...batchMeta,
    warnings,
    errors: [],
  };
}

async function fetchAllMatchingIds(collectionName, filter) {
  const ids = [];
  let cursor = null;
  while (true) {
    const page = await fetchMatchingPage(collectionName, filter, { pageSize: 500, cursor });
    ids.push(...page.rows.map((r) => r.id));
    if (!page.hasMore) break;
    cursor = page.cursor;
  }
  return ids;
}

export async function executeUpdateByCondition({
  collectionName,
  filter,
  updateRequest,
  adminUid,
  adminName,
  onProgress,
}) {
  const col = collectionName.trim();
  const started = Date.now();
  await ensureFreshAuthToken();
  await registerCollection(col, { createdBy: adminUid || adminName || null });
  await touchCollection(col, adminUid || adminName || null);

  onProgress?.({ phase: "fetching", message: "Loading matching documents…" });
  const ids = await fetchAllMatchingIds(col, filter);
  if (!ids.length) {
    return { updated: 0, failed: 0, seconds: 0, errors: [] };
  }

  const documents = {};
  for (let i = 0; i < ids.length; i += 100) {
    const chunk = ids.slice(i, i + 100);
    await Promise.all(
      chunk.map(async (id) => {
        const snap = await getDoc(doc(db, col, id));
        if (snap.exists()) documents[id] = snap.data();
      })
    );
    onProgress?.({ phase: "fetching", message: `Loaded ${Math.min(i + 100, ids.length)} / ${ids.length}` });
  }

  onProgress?.({ phase: "backup", message: `Backing up ${ids.length} document(s)…` });
  await createBackupsBatched({
    collectionName: col,
    documents,
    adminUid,
    adminName,
    operation: "update_by_condition",
    chunkSize: 100,
  });

  await ensureFreshAuthToken();

  const totalBatches = Math.ceil(ids.length / DELETE_BATCH_SIZE);
  let updated = 0;
  let failed = 0;
  const errors = [];

  for (let i = 0; i < ids.length; i += DELETE_BATCH_SIZE) {
    const batchNum = Math.floor(i / DELETE_BATCH_SIZE) + 1;
    const chunkIds = ids.slice(i, i + DELETE_BATCH_SIZE);
    onProgress?.({
      phase: "updating",
      batch: batchNum,
      totalBatches,
      message: `Updating… Batch ${batchNum} of ${totalBatches}`,
    });

    const operations = chunkIds.map((id) => ({
      type: "update",
      ref: doc(db, col, id),
      data: applyUpdateTransform(documents[id] || {}, updateRequest),
    }));

    try {
      await commitBatches(operations);
      updated += chunkIds.length;
    } catch (err) {
      failed += chunkIds.length;
      errors.push(err?.message || String(err));
    }
  }

  return {
    updated,
    failed,
    seconds: Math.round((Date.now() - started) / 1000),
    errors: [...new Set(errors)].slice(0, 5),
  };
}
